import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Loader2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function generateSessionId() {
  return 'chat_' + Math.random().toString(36).substring(2, 15);
}

export default function ChatBot({ isOpen, setIsOpen }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm the USG AI assistant. I can help you with information about our utility locating services, answer questions about safety, or assist you in requesting a quote. How can I help you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage
      });
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly at info@usgllc811.com." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickQuestions = [
    "What services do you offer?",
    "How can I request a quote?",
    "How do I schedule a locate?",
    "Do you offer emergency services?"
  ];

  return (
    <>
      {/* Chat Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-24 right-6 w-16 h-16 bg-skytec-secondary rounded-full flex items-center justify-center shadow-lg shadow-skytec-secondary/30 z-50 group"
            data-testid="chat-toggle-btn"
          >
            <MessageCircle className="w-7 h-7 text-white" />
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-skytec-primary rounded-full flex items-center justify-center">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-24 right-6 w-96 h-[550px] max-h-[75vh] bg-white rounded-2xl shadow-2xl border border-skytec-border overflow-hidden z-50 flex flex-col"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-skytec-primary p-4 border-b border-skytec-border flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white">USG Assistant</h3>
                  <div className="flex items-center space-x-1">
                    <span className="w-2 h-2 bg-skytec-accent rounded-full animate-pulse" />
                    <span className="text-xs text-white/80">Online</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="w-5 h-5 text-white/80" />
              </button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex items-start space-x-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.role === 'user' 
                          ? 'bg-skytec-secondary-dim' 
                          : 'bg-skytec-primary-dim'
                      }`}>
                        {message.role === 'user' 
                          ? <User className="w-4 h-4 text-skytec-secondary" />
                          : <Bot className="w-4 h-4 text-skytec-primary" />
                        }
                      </div>
                      <div className={`rounded-2xl px-4 py-3 ${
                        message.role === 'user'
                          ? 'bg-skytec-secondary text-white rounded-tr-sm'
                          : 'bg-skytec-surface-highlight text-skytec-fg rounded-tl-sm'
                      }`}>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-skytec-primary-dim rounded-full flex items-center justify-center">
                        <Bot className="w-4 h-4 text-skytec-primary" />
                      </div>
                      <div className="bg-skytec-surface-highlight rounded-2xl rounded-tl-sm px-4 py-3">
                        <div className="flex space-x-1">
                          <span className="w-2 h-2 bg-skytec-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-skytec-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-skytec-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Quick Questions */}
              {messages.length === 1 && (
                <div className="mt-6">
                  <p className="text-xs text-skytec-fg/40 mb-3 font-mono uppercase tracking-wider">Quick Questions</p>
                  <div className="flex flex-wrap gap-2">
                    {quickQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setInput(question);
                          inputRef.current?.focus();
                        }}
                        className="text-xs px-3 py-2 bg-skytec-bg border border-skytec-border rounded-full text-skytec-fg/70 hover:text-skytec-primary hover:border-skytec-primary transition-colors"
                        data-testid={`quick-question-${index}`}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-skytec-border bg-skytec-surface-highlight">
              <div className="flex space-x-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 bg-white border-skytec-border text-skytec-fg placeholder:text-skytec-fg/40"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  disabled={isLoading || !input.trim()}
                  className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 disabled:opacity-50"
                  data-testid="chat-send-btn"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
